import IconPhone from 'assets/icons/call.svg';
import IconMagazine from 'assets/icons/news-mode.svg';
import cn from 'classnames';
import LanguageSelector from 'components/language-selector';
import PetProfileMobileNavigationLink from 'components/pet-profile/mobile-navigation-link';
import { useConfig } from 'config/use-config';
import { useSegmentationContext } from 'contexts/segmentation';
import useLockBodyScroll from 'hooks/common/use-lock-body-scroll';
import useNavigationData from 'hooks/navigation/use-navigation-data';
import { NavigationItem } from 'interfaces/navigation-data';
import Link from 'next/link';
import { FC, ReactNode, useState } from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Gtm } from 'tracking';
import IconChevronLeft from '../../../../assets/icons/chevron-left-stroke.svg';
import IconMenuClose from '../../../../assets/icons/menu-close.svg';
import IconMenu from '../../../../assets/icons/menu.svg';
import NavigationLink from '../navigation-link';
import { useRootStore } from 'stores/use-root-store';
import { PetType } from 'constants/pet-type';
import { getSegmentationFromUrl } from 'utils/url/get-segmentation-from-url';

const ComponentName = 'Navigation';

interface PropTypes {
  className?: string;
}

/**
 * @TODO : Too many repetitive codes. Should be refactored
 */ const Mobile: FC<PropTypes> = ({ className }) => {
  const { commonPagePaths, featureFlags } = useConfig();
  const featureFlag = featureFlags.petProfile;
  const [visible, setVisibility] = useState(false);
  const intl = useIntl();
  const { mobile } = useNavigationData();
  const petType = useRootStore((store) => store.petType);
  const setPetType = useRootStore((store) => store.setPetType);
  const { pushSegmentation } = useSegmentationContext();
  const [subNav, setSubNav] = useState(null as null | NavigationItem);

  useLockBodyScroll(visible);
  const navigationItem =
    (className: string, petType: string, subNav?: NavigationItem) =>
    // eslint-disable-next-line react/display-name
    (item: NavigationItem, index: number) => {
      const rootLabel = intl.formatMessage({
        id: `header:navigation-${petType}`,
      });

      return (
        <NavigationLink
          key={index}
          item={item}
          className={className}
          onClick={() => {
            if (item.itemType === 'entry' || item.itemType === 'card') {
              setVisibility(false);
            } else {
              setSubNav(item);
            }

            if ('url' in item && item.url) {
              const segmentation = getSegmentationFromUrl({
                path: item.url,
              });

              if (segmentation) {
                pushSegmentation({
                  segmentation: segmentation.id,
                  isUserAction: true,
                });
              }
            }

            if (item.title) {
              Gtm.track.selectElement({
                element: subNav
                  ? [ComponentName, rootLabel, String(subNav.title), item.title]
                  : [ComponentName, rootLabel, item.title],
              });
            }
          }}
        />
      );
    };

  const navigationTree = (pet: PetType): ReactNode => {
    return (
      <>
        <div className="flex grow flex-col overflow-x-hidden overflow-y-scroll">
          {featureFlag && (
            <PetProfileMobileNavigationLink setVisibility={setVisibility} petType={pet} />
          )}

          {mobile[pet].map((nav, index) => {
            return (
              <div key={index} className="bg-other-white pt-4 pb-16">
                {nav.map((item, itemIndex) => {
                  // Render language selector instead of the languageSwitcher placeholder
                  if (item.title === 'languageSwitcher') {
                    const hasReducedTextSize = 'reducedTextSize' in item && item.reducedTextSize;
                    return (
                      <NavigationLink
                        key={itemIndex}
                        item={item}
                        useItemWrapper={false}
                        onClick={() => {}}
                        className={cn(
                          'cursor-default justify-start',
                          hasReducedTextSize && 'text-xs font-normal'
                        )}
                      >
                        <LanguageSelector />
                      </NavigationLink>
                    );
                  }
                  return navigationItem('font-black', pet)(item, itemIndex);
                })}
              </div>
            );
          })}
        </div>
      </>
    );
  };

  const navigation = (): ReactNode => {
    return (
      <>
        <div
          data-test="mobile-navigation"
          className={cn(
            'z-mobile-navigation bg-other-white fixed top-0 bottom-0 left-0 flex h-full w-full transform flex-col items-stretch justify-between overflow-hidden overflow-x-hidden transition-transform duration-300 ease-in-out lg:hidden',
            visible ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          <div className="text-other-black w-full text-sm tracking-wide uppercase">
            <div className="bg-primary-main flex h-13 items-center justify-between px-4">
              <div className="flex gap-4">
                <button
                  data-test="mobile-nav-dogs"
                  className="relative font-black capitalize focus:outline-hidden"
                  onClick={() => {
                    Gtm.track.selectElement({
                      element: [
                        ComponentName,
                        intl.formatMessage({
                          id: 'header:navigation-dogs',
                        }),
                      ],
                    });

                    setPetType(PetType.Dog);
                  }}
                >
                  <span
                    className={`border-other-white text-other-white relative text-base ${
                      petType !== PetType.Dog && 'text-grey-125'
                    }`}
                  >
                    <FormattedMessage id="header:navigation-dogs" />
                    {petType === PetType.Dog && (
                      <div className="border-other-white bg-other-white absolute -bottom-1 left-0 h-0.75 w-full rounded-lg border" />
                    )}
                  </span>
                </button>
                <button
                  data-test="mobile-nav-cats"
                  className="relative font-black capitalize focus:outline-hidden"
                  onClick={() => {
                    Gtm.track.selectElement({
                      element: [
                        ComponentName,
                        intl.formatMessage({ id: 'header:navigation-cats' }),
                      ],
                    });
                    setPetType(PetType.Cat);
                  }}
                >
                  <span
                    className={`border-other-white text-other-white relative text-base ${
                      petType !== PetType.Cat && 'text-grey-125'
                    }`}
                  >
                    <FormattedMessage id="header:navigation-cats" />
                    {petType === PetType.Cat && (
                      <div className="border-other-white bg-other-white absolute -bottom-1 left-0 h-0.75 w-full rounded-lg border" />
                    )}
                  </span>
                </button>
              </div>
              <button
                data-test="mobile-navigation-close"
                className="flex h-6 w-6 items-center justify-center focus:outline-hidden"
                onClick={() => {
                  setVisibility(false);
                  setSubNav(null);
                }}
              >
                <IconMenuClose className="text-other-white h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div className="flex grow overflow-x-hidden overflow-y-auto">
            <div
              className={cn(
                'bg-other-white flex h-full min-w-full transform flex-col transition-transform duration-300 ease-in-out',
                petType === PetType.Cat && '-translate-x-full'
              )}
              data-test="mobile-navigation-dog-column"
            >
              {navigationTree(PetType.Dog)}
            </div>
            <div
              className={cn(
                'bg-other-white flex h-full min-w-full transform flex-col transition-transform duration-300 ease-in-out',
                petType === PetType.Cat && '-translate-x-full'
              )}
              data-test="mobile-navigation-cat-column"
            >
              {navigationTree(PetType.Cat)}
            </div>
          </div>

          <div
            className={cn(
              'bg-other-white absolute top-0 h-full min-w-full transform overflow-auto leading-[1.4rem] transition-transform duration-300 ease-in-out',
              subNav === null && '-translate-x-full'
            )}
          >
            <button
              className="bg-primary-main text-other-white mb-2 flex w-full items-center py-5 pr-3 pl-4 font-normal focus:outline-hidden"
              onClick={() => {
                setSubNav(null);
              }}
            >
              <IconChevronLeft className="mb-1 h-6 w-4" />
              <FormattedMessage id="header:navigation:mobile:back" />
            </button>
            {petType &&
              subNav !== null &&
              (subNav.itemType === 'nested-card' || subNav.itemType === 'nested-entry') && (
                <>
                  {subNav.items && (
                    <div className="py-4">
                      <div className="text-primary-main mb-2 px-4 text-lg font-black">
                        <FormattedMessage id={subNav.title} />
                      </div>
                      {subNav.items.map(navigationItem('px8', petType, subNav))}
                    </div>
                  )}
                </>
              )}
          </div>
          <div className="bg-background-default text-primary-main absolute bottom-0 flex w-full items-center justify-between p-4">
            {featureFlags.magazin && (
              <Link
                href="/magazin"
                className="border-primary-outlined-resting-border mx-2 flex h-8 w-2/3 items-center justify-center rounded-full border px-2 py-1 text-sm font-medium"
                onClick={() => {
                  Gtm.track.selectElement({
                    element: [
                      ComponentName,
                      intl.formatMessage({
                        id: 'header:navigation:mobile:magazine',
                      }),
                    ],
                  });
                  setVisibility(false);
                }}
              >
                <IconMagazine className="mr-2 w-5" />
                <FormattedMessage id="header:navigation:mobile:magazine" />
              </Link>
            )}

            <Link
              href={commonPagePaths.foodConsultation}
              className={cn(
                'border-primary-outlined-resting-border mx-2 flex h-8 w-2/3 items-center justify-center rounded-full border px-2 py-1 text-sm font-medium',
                featureFlags.magazin ? 'w-2/3' : 'w-full'
              )}
              onClick={() => {
                Gtm.track.selectElement({
                  element: [
                    ComponentName,
                    intl.formatMessage({
                      id: 'header:navigation:mobile:consulting',
                    }),
                  ],
                });

                setVisibility(false);
              }}
            >
              <IconPhone className="mr-2 w-5" />
              <FormattedMessage id="header:navigation:mobile:consulting" />
            </Link>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <button
        aria-label={intl.formatMessage({ id: 'navigation:mobile:aria-label' })}
        data-test="mobile-navigation-button"
        className={cn(
          className,
          'flex flex-col items-center justify-center leading-none focus:outline-hidden lg:hidden'
        )}
        onClick={() => {
          if (!visible) {
            Gtm.track.selectElement({ element: ComponentName });
          }
          setVisibility((visible) => !visible);
        }}
      >
        {visible ? (
          <IconMenuClose className="text-primary-main h-auto w-5" />
        ) : (
          <IconMenu className="text-primary-main h-3 w-4" />
        )}
      </button>
      {ReactDOM.createPortal(navigation(), document.body)}
    </>
  );
};

export default Mobile;
